import type { ReactNode } from "react";

/** 체크박스. 약관 동의 등. */
export function Checkbox({
  checked,
  onChange,
  children,
  className = "",
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`flex cursor-pointer items-start gap-3 ${className}`}>
      <span
        className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-[6px] border transition-colors ${
          checked ? "border-primary-900 bg-primary-900" : "border-gray-500 bg-white"
        }`}
      >
        {checked && (
          <svg viewBox="0 0 16 16" className="size-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="2.4">
            <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm text-gray-800">{children}</span>
    </label>
  );
}

/** 토글 칩. 다중 선택에 쓴다. */
export function Chip({
  selected,
  onClick,
  children,
  disabled,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`inline-flex h-10 items-center gap-1.5 rounded-full border px-4 text-sm font-medium transition-colors disabled:opacity-40 ${
        selected
          ? "border-primary-900 bg-primary-40 text-primary-900"
          : "border-gray-400 bg-white text-gray-700 hover:border-gray-500"
      }`}
    >
      {selected && (
        <svg viewBox="0 0 16 16" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2.4">
          <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {children}
    </button>
  );
}

/** 단일 선택 카드 목록. 기업규모, 나라장터 등록 여부 등. */
export function OptionCards<T extends string>({
  value,
  onChange,
  options,
  columns = 2,
}: {
  value: T | null | undefined;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode; description?: ReactNode }[];
  columns?: 1 | 2 | 4;
}) {
  const grid = columns === 1 ? "grid-cols-1" : columns === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-2 sm:grid-cols-4";
  return (
    <div role="radiogroup" className={`grid gap-3 ${grid}`}>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={`flex flex-col items-start gap-1 rounded-md border p-4 text-left transition-colors ${
              on ? "border-primary-900 bg-primary-40 ring-1 ring-primary-900" : "border-gray-400 bg-white hover:border-gray-500"
            }`}
          >
            <span className={`text-base font-semibold ${on ? "text-primary-900" : "text-gray-900"}`}>{o.label}</span>
            {o.description && <span className="text-sm text-gray-600">{o.description}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** 세그먼트 버튼. 보안 조건 3지선다 등. */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T | null | undefined;
  onChange: (v: T) => void;
  options: { value: T; label: string; tone?: "positive" | "negative" | "neutral" }[];
}) {
  return (
    <div role="radiogroup" className="grid grid-cols-3 gap-1 rounded-md bg-gray-300 p-1">
      {options.map((o) => {
        const on = value === o.value;
        const tone =
          o.tone === "positive" ? "text-positive" : o.tone === "negative" ? "text-negative" : "text-gray-800";
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={`h-9 rounded-sm text-sm font-semibold transition-colors ${
              on ? `bg-white shadow-card ${tone}` : "text-gray-600 hover:text-gray-800"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
